import { useEffect, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import dynamic from 'next/dynamic';

import omit from 'lodash/omit';

import useSocialProfiles from '@/hooks/swr/useSocialProfiles';
import useMedias from '@/utils/hooks/swr/useMedias';
import useTranslation from '@/utils/hooks/translation/useTranslation';
import useAcl from '@/utils/hooks/user/useAcl';
import useActiveSocialProfile from '@/utils/hooks/user/useActiveSocialProfile';

import faqUrl from '@/utils/urls/faqUrl';
import useLabelsAlbumsOnMedia from '@/utils/hooks/utils/useLabelsAlbumsOnMedia';
import useRouterQuery from '@/utils/hooks/utils/useRouterQuery';

import PropTypes from 'prop-types';

import { aclFeatures } from '@/lib/acl/aclFeatures';
import {
    TAB_POST_FACEBOOK,
    TAB_POST_INSTAGRAM,
    TAB_POST_LINKEDIN,
    TAB_TWEETS_TWITTER
} from '@/lib/myprofile/tabs';
import { CUSTOM } from '@/lib/periods';
import { postSortOptions } from '@/lib/posts/postSortOptions';
import { postsSortTypes } from '@/lib/posts/postSortTypes';
import { EXPORT_ENDPOINT_TYPES, POST } from '@/lib/posts/postTypes';
import { CONTENT_POSTS } from '@/lib/routes/privateRoutes';
import { FACEBOOK, INSTAGRAM, LINKEDIN, TWITTER } from '@/lib/socialProfile';
import { url_faq_estimated_followers } from '@/lib/urls/faqUrls';

import Carousel from '@/components/base/Carousel';
import LinkBase from '@/components/base/LinkBase';
import Loader from '@/components/base/Loader';
import { PostComponent } from '@/components/base/Post/PostComponent';
import { WidgetMedia } from '@/components/base/WidgetMedia';
import ButtonDownloadDirectExport from '@/components/composite/Button/ButtonDownloadDirectExport';
import CustomDashboardWidgetActions from '@/components/pages/custom-dashboards/dashboard/CustomDashboardWidgetActions';
import { Trans } from '@/components/tools/translation';
const CustomDashboardWidgetOptionSaver = dynamic(
    () => import('@/components/pages/custom-dashboards/dashboard/CustomDashboardWidgetOptionSaver')
);
const MediaViewerModal = dynamic(
    () => import('@/components/composite/MediaViewer/MediaViewerModal')
);

const getActiveTab = (network) => {
    switch (network) {
        case INSTAGRAM:
            return TAB_POST_INSTAGRAM;
        case FACEBOOK:
            return TAB_POST_FACEBOOK;
        case LINKEDIN:
            return TAB_POST_LINKEDIN;
        case TWITTER:
            return TAB_TWEETS_TWITTER;
    }
};

export const MediasWidget = ({
    periodData,
    sortFields,
    page,
    numberFormat,
    onDelete,
    config,
    isDashboard = false
}) => {
    const { hasAcl } = useAcl();
    const { formatUrlWithQuery } = useRouterQuery();

    const { activeSocialProfile } = useActiveSocialProfile();
    const { activatedSocialProfiles } = useSocialProfiles({
        revalidateOnMount: false,
        shouldRetryOnError: false,
        revalidateOnFocus: false,
        revalidateOnReconnect: false
    });
    const [isLoadingExportXlsDashboard, setIsLoadingExportXlsDashboard] = useState(false);
    const [mediaViewerParams, setMediaViewerParams] = useState({
        isOpen: false,
        mediaId: null,
        socialProfile: null
    });
    const [socialProfileSelected, setSocialProfileSelected] = useState(
        isDashboard
            ? activatedSocialProfiles.find((sp) => sp.id === config.social_profiles[0])
            : null
    );
    const currentSocialProfile = isDashboard ? socialProfileSelected : activeSocialProfile;
    const network = currentSocialProfile?.social_network_type;

    const { t } = useTranslation('commons');
    const sortOptions = postSortOptions(t, page, config?.noSortTypesSelect)
        ?.[network]?.filter((element) => sortFields?.includes(element?.value))
        ?.sort((a, b) => sortFields?.indexOf(a?.value) - sortFields?.indexOf(b?.value));

    const sortTypes = postsSortTypes(t)[network];

    const {
        dataMedias,
        requestParams,
        mediasError,
        // Loaders
        isLoadingInitialData,
        // Functions
        mutateMedias,
        setRequestParams
    } = useMedias({
        activeSPID: currentSocialProfile?.id,
        limit: 10
    });

    useEffect(() => {
        if (periodData.period === CUSTOM) {
            setRequestParams((prevState) => ({
                ...prevState,
                period: periodData.period,
                start: periodData.start,
                end: periodData.end,
                comparison_period: periodData.compareTo,
                with_labels: true,
                with_albums: true
            }));
        } else {
            setRequestParams((prevState) => ({
                ...omit(prevState, ['start', 'end']),
                period: periodData.period,
                comparison_period: periodData.compareTo,
                with_labels: true,
                with_albums: true
            }));
        }
    }, [periodData.period, periodData.start, periodData.end, periodData.compareTo]);

    // relaunch request if active SocialProfile changes
    useEffect(() => {
        if (
            sortTypes?.filter((el) => requestParams?.filter_post_type?.includes(el?.value))
                ?.length === 0
        ) {
            setRequestParams((prevState) => ({
                ...prevState,
                filter_post_type: []
            }));
        }

        if (
            sortOptions?.filter((el) => {
                return requestParams?.sort?.column === el?.value;
            })?.length === 0
        ) {
            setRequestParams((prevState) => ({
                ...prevState,
                sort: {
                    column: sortOptions?.[0]?.value,
                    order: sortOptions?.[0]?.order
                }
            }));
        }
    }, [activeSocialProfile?.id, network]);

    useEffect(() => {
        if (config?.parameters) {
            setRequestParams((prevState) => ({
                ...prevState,
                ...config.parameters
            }));
        }
    }, []);

    const { handleAlbumsEvent, handleLabelsEvent } = useLabelsAlbumsOnMedia({
        mutateMedias,
        requestParams
    });

    const canExportMedias =
        !isLoadingInitialData && dataMedias && dataMedias[0]?.medias?.length > 0;

    const onClickDashboardXlsExport = async (callback) => {
        setIsLoadingExportXlsDashboard(true);
        await callback();
        setIsLoadingExportXlsDashboard(false);
    };

    return (
        <WidgetMedia>
            <WidgetMedia.Header>
                <div className="flex items-center space-x-3">
                    {isDashboard ? (
                        <>
                            <div>
                                <CustomDashboardWidgetActions.SelectSP
                                    config={config}
                                    socialProfiles={config.social_profiles}
                                    setSocialProfileSelected={setSocialProfileSelected}
                                    isMedia
                                />
                            </div>
                            <CustomDashboardWidgetOptionSaver>
                                {(saveWidgetOption) => (
                                    <>
                                        <div className="w-52">
                                            <WidgetMedia.SortBy
                                                network={network}
                                                setRequestParams={setRequestParams}
                                                data={sortOptions}
                                                currentSort={
                                                    config?.parameters
                                                        ? config?.parameters?.sort
                                                        : requestParams?.sort
                                                }
                                                onChange={(sort) => {
                                                    saveWidgetOption(
                                                        config,
                                                        'sort',
                                                        {
                                                            order: sort?.order,
                                                            column: sort?.value
                                                        },
                                                        {
                                                            order: sortOptions[0].order,
                                                            column: sortOptions[0].value
                                                        }
                                                    );
                                                }}
                                            />
                                        </div>
                                        {config?.noSortTypesSelect ? null : (
                                            <div className="w-36">
                                                <WidgetMedia.SelectType
                                                    setRequestParams={setRequestParams}
                                                    currentFilter={
                                                        config?.parameters
                                                            ? config?.parameters?.filter_post_type
                                                            : requestParams?.filter_post_type
                                                    }
                                                    network={network}
                                                    data={sortTypes}
                                                    onChange={(filter_post_type) =>
                                                        saveWidgetOption(
                                                            config,
                                                            'filter_post_type',
                                                            filter_post_type,
                                                            []
                                                        )
                                                    }
                                                />
                                            </div>
                                        )}
                                    </>
                                )}
                            </CustomDashboardWidgetOptionSaver>
                        </>
                    ) : (
                        <>
                            <div className="w-52">
                                <WidgetMedia.SortBy
                                    network={network}
                                    setRequestParams={setRequestParams}
                                    data={sortOptions}
                                />
                            </div>
                            {config?.noSortTypesSelect ? null : (
                                <div className="w-36">
                                    <WidgetMedia.SelectType
                                        setRequestParams={setRequestParams}
                                        currentFilter={
                                            config?.parameters
                                                ? config?.parameters?.filter_post_type
                                                : requestParams?.filter_post_type
                                        }
                                        network={network}
                                        data={sortTypes}
                                    />
                                </div>
                            )}
                        </>
                    )}
                </div>
                {isDashboard ? (
                    <CustomDashboardWidgetActions.Popover className="bg-white dark:bg-icoDarkMode-deepBlack">
                        {canExportMedias ? (
                            <li className="rounded font-GMedium text-xs text-icoGray-500 transition-all hover:bg-icoBlue-100 hover:text-icoBlue dark:hover:bg-icoDarkMode-deepBlack">
                                <ButtonDownloadDirectExport
                                    sortOptions={sortOptions}
                                    isLoading={isLoadingExportXlsDashboard}
                                    requestParams={requestParams}
                                    typeExport={EXPORT_ENDPOINT_TYPES[POST]}
                                    currentSocialProfile={currentSocialProfile}>
                                    {({ generateReport, isLoading }) => (
                                        <button
                                            disabled={isLoading}
                                            className="flex w-full items-center p-2 disabled:text-icoGray-500 disabled:dark:text-icoDarkMode-wolf"
                                            onClick={() =>
                                                onClickDashboardXlsExport(generateReport)
                                            }>
                                            {isLoading ? <Loader className="mr-1 h-3 w-3" /> : null}
                                            {t('analytics:xls_export')}
                                        </button>
                                    )}
                                </ButtonDownloadDirectExport>
                            </li>
                        ) : null}
                        <li className="rounded font-GMedium text-xs text-icoGray-500 transition-all hover:bg-icoBlue-100 hover:text-icoBlue dark:hover:bg-icoDarkMode-deepBlack">
                            <button className="w-full p-2 text-left" onClick={onDelete}>
                                {t('dashboard:remove_widget')}
                            </button>
                        </li>
                    </CustomDashboardWidgetActions.Popover>
                ) : canExportMedias ? (
                    <ButtonDownloadDirectExport
                        sortOptions={sortOptions}
                        requestParams={requestParams}
                        typeExport={EXPORT_ENDPOINT_TYPES[POST]}
                        currentSocialProfile={currentSocialProfile}>
                        {({ generateReport, isLoading }) => (
                            <WidgetMedia.ExportButton
                                onClick={generateReport}
                                isLoading={isLoading}
                                tooltipContent={t('analytics:xls_export')}
                            />
                        )}
                    </ButtonDownloadDirectExport>
                ) : null}
            </WidgetMedia.Header>

            {isLoadingInitialData && !dataMedias ? (
                <WidgetMedia.Placeholder
                    placeholderConfig={{ network }}
                    wrapperClass="xl:space-x-m 2xl:space-x-5"
                    postClass="min-w-[calc(33.3%-6px)] xl:min-w-[calc(25%-13.8px)] 2xl:min-w-[calc(20%-16px)]"
                    postFormat="square"
                />
            ) : null}
            {!isLoadingInitialData && dataMedias && dataMedias[0]?.medias?.length === 0 ? (
                <WidgetMedia.NoData
                    networks={[network]}
                    message={
                        dataMedias[0]?.not_full_medias ? (
                            <Trans
                                i18nKey="analytics:not_full_medias_empty"
                                components={{
                                    tag0: (
                                        <LinkBase
                                            key="learn_more"
                                            className="underline"
                                            target="_blank"
                                            href={faqUrl(url_faq_estimated_followers)}
                                            rel="help noopener noreferrer"
                                        />
                                    )
                                }}
                            />
                        ) : (
                            t('analytics:no_posts_in_period')
                        )
                    }
                />
            ) : null}

            {mediasError && !isLoadingInitialData ? (
                <WidgetMedia.ErrorFallBack resetErrorBoundary={() => mutateMedias()} />
            ) : null}

            {!isLoadingInitialData && dataMedias && dataMedias[0]?.medias?.length > 0 ? (
                <ErrorBoundary
                    FallbackComponent={WidgetMedia.ErrorFallBack}
                    onReset={() => mutateMedias()}>
                    <WidgetMedia.Carousel
                        navPosition="external"
                        className="xl:space-x-m 2xl:space-x-5">
                        {dataMedias?.map((medias) => {
                            return medias?.['medias']?.map((media, index) => {
                                // Disable promoted_post if the user doesn't have the ACL
                                let promoted_post = false;
                                if (hasAcl(aclFeatures.analytic.promoted_post.read)) {
                                    promoted_post = media?.promoted_post ?? false;
                                }

                                return (
                                    <Carousel.Slide
                                        key={media?.id}
                                        arial-label={`media ${media?.id}`}
                                        className="min-w-[calc(33.3%-6px)] max-w-[calc(33.3%-6px)] xl:min-w-[calc(25%-13.8px)] xl:max-w-[calc(25%-13.8px)] 2xl:min-w-[calc(20%-16px)] 2xl:max-w-[calc(20%-16px)]">
                                        <PostComponent
                                            data-testid={`post-${index}`}
                                            network={network}
                                            type="medias"
                                            isLoading={false}
                                            data={{ ...media, promoted_post }}
                                            config={{
                                                postType: POST,
                                                postFormat: 'square',
                                                activeFilter: requestParams?.sort?.column ?? null,
                                                network,
                                                numberFormat: numberFormat
                                            }}
                                            handleLabelsEvent={handleLabelsEvent}
                                            handleAlbumsEvent={handleAlbumsEvent}
                                            openViewer={(e) => {
                                                setMediaViewerParams({
                                                    isOpen: true,
                                                    mediaId: media?.id,
                                                    socialProfile: currentSocialProfile
                                                });
                                                e.stopPropagation();
                                            }}
                                        />
                                    </Carousel.Slide>
                                );
                            });
                        })}
                        <Carousel.Slide
                            arial-label="show-more"
                            className="group min-w-[calc(33.3%-6px)] max-w-[calc(33.3%-6px)] xl:min-w-[calc(25%-13.8px)] xl:max-w-[calc(25%-13.8px)] 2xl:min-w-[calc(20%-16px)] 2xl:max-w-[calc(20%-16px)]">
                            <WidgetMedia.ShowMore
                                url={formatUrlWithQuery(CONTENT_POSTS, {
                                    tab: getActiveTab(network),
                                    column: requestParams?.sort?.column,
                                    order: requestParams?.sort?.order,
                                    analytics: true,
                                    ...(isDashboard && {
                                        socialProfileId: socialProfileSelected.id
                                    }),
                                    ...(requestParams?.filter_post_type?.length > 0 && {
                                        filterPostType: JSON.stringify(
                                            requestParams.filter_post_type
                                        )
                                    })
                                })}
                                textContent={t('show_more_posts')}
                            />
                        </Carousel.Slide>
                    </WidgetMedia.Carousel>
                    {dataMedias[0]?.not_full_medias ? (
                        <div className="font-GMedium text-xs text-icoGray-500 dark:text-icoDarkMode-wolf">
                            <Trans
                                i18nKey="analytics:not_full_medias"
                                components={{
                                    tag0: (
                                        <LinkBase
                                            key="learn_more"
                                            target="_blank"
                                            className="underline"
                                            href={faqUrl(url_faq_estimated_followers)}
                                            rel="help noopener noreferrer"
                                        />
                                    )
                                }}
                            />
                        </div>
                    ) : null}
                </ErrorBoundary>
            ) : null}
            <MediaViewerModal
                isOpen={mediaViewerParams?.isOpen}
                toggle={() => {
                    setMediaViewerParams({ ...mediaViewerParams, isOpen: false });
                }}
                mediaId={mediaViewerParams?.mediaId}
                socialProfile={mediaViewerParams?.socialProfile}
                mediaType={POST}
                mutateMedias={mutateMedias}
                mediaRequestParams={requestParams}
            />
        </WidgetMedia>
    );
};

MediasWidget.propTypes = {
    sortFields: PropTypes.arrayOf(PropTypes.string),
    page: PropTypes.string,
    periodData: PropTypes.object,
    numberFormat: PropTypes.string
};
